import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { NETZ_FEHLER_TEXT, istNetzFehler } from '../netzFehler';
import { ladeAlleSeiten } from '../seitenweise';
import { istMaterialToken } from './materialKarte';
import { VERLAUF_GRENZE, type ArtikelWerte, type StatusZiel } from './materialLogic';
import type { MaterialArtikel, MaterialEmpfaengerAnzeige, MaterialMeldung, MaterialStatus } from './materialTypes';

/**
 * Datenbankzugriff des Material-Bereichs (docs/materialverwaltung-plan.md,
 * Client 5.): liest `material_artikel` und `material_meldungen` (Nr. 178,
 * RLS: eigene Firma, nur mit `material.beschaffen` oder `material.verwalten`)
 * und schreibt ausschließlich über die RPCs aus Nr. 179.
 *
 * FAIL-OPEN BEIM LESEN. Fehlt das SQL in dieser Umgebung (Tabelle 42P01/
 * PGRST205, Funktion PGRST202/42883, HTTP 404), liefert `ladeMaterialStand`
 * den Zustand `nicht_eingerichtet` — der Bereich zeigt einen Hinweis statt
 * abzustürzen. `zaehleOffeneMaterialMeldungen` (Badge im Menü) gibt bei
 * JEDEM Fehler null.
 *
 * SCHREIBEN WIRFT. Jeder Fehler eines Schreibwegs kommt als `MaterialFehler`
 * mit deutschem Satz; die Oberfläche zeigt ihn dort, wo geklickt wurde.
 *
 * `supabase` ist im Bestand untypisiert; jede Antwort wird deshalb als
 * `unknown` gelesen und von Hand geprüft.
 */

const T_ARTIKEL = 'material_artikel';
const T_MELDUNGEN = 'material_meldungen';

/** Alle Spalten aus Nr. 178, die der Bereich braucht — ausdrücklich, kein `*`. */
const ARTIKEL_SPALTEN = 'id, company_id, name, lagerort, hinweis, qr_token, aktiv, erstellt_am, geaendert_am';
const MELDUNG_SPALTEN =
    'id, artikel_id, company_id, gemeldet_von, melder_name, gemeldet_am, anzahl, zuletzt_ausgeloest_am, status, '
    + 'bestellt_von, bestellt_am, erledigt_von, erledigt_am, mail_gesendet_am, mail_fehler';

// ─── Fehler ─────────────────────────────────────────────────────────────────

export type MaterialFehlerCode = 'nicht_eingerichtet' | 'kein_recht' | 'nicht_gefunden' | 'ungueltig' | 'netz' | 'unbekannt';

export class MaterialFehler extends Error {
    readonly code: MaterialFehlerCode;
    constructor(code: MaterialFehlerCode, text: string) {
        super(text);
        this.name = 'MaterialFehler';
        this.code = code;
    }
}

export const MATERIAL_NICHT_EINGERICHTET_TEXT =
    'Materialverwaltung ist in dieser Umgebung noch nicht eingerichtet (SQL Nr. 178/179 fehlt).';

const ERSATZ_TEXT: Record<MaterialFehlerCode, string> = {
    nicht_eingerichtet: MATERIAL_NICHT_EINGERICHTET_TEXT,
    kein_recht: 'Dafür fehlt das Recht („Materialmeldungen bearbeiten“ bzw. „Material verwalten“).',
    nicht_gefunden: 'Nicht gefunden — bitte die Liste neu laden.',
    ungueltig: 'Die Angabe ist ungültig — bitte prüfen und neu laden.',
    netz: NETZ_FEHLER_TEXT,
    unbekannt: 'Der Vorgang ist fehlgeschlagen.',
};

const feld = (o: unknown, schluessel: string): unknown =>
    o !== null && typeof o === 'object' ? (o as Record<string, unknown>)[schluessel] : undefined;

const fehlerCode = (e: unknown): string => {
    const c = feld(e, 'code');
    return typeof c === 'string' ? c : typeof c === 'number' ? String(c) : '';
};

const fehlerText = (e: unknown): string => {
    const m = feld(e, 'message');
    return typeof m === 'string' ? m.trim() : '';
};

/** SQL Nr. 178/179 fehlt: Tabelle (42P01/PGRST205), Funktion (PGRST202/42883) oder HTTP 404. */
export const istNichtEingerichtet = (e: unknown, httpStatus?: number): boolean => {
    const code = fehlerCode(e);
    if (code === '42P01' || code === 'PGRST205' || code === 'PGRST202' || code === '42883') return true;
    if (httpStatus === 404 || feld(e, 'status') === 404) return true;
    return /could not find the (function|table)/i.test(fehlerText(e));
};

/**
 * Irgendein Fehler → MaterialFehler. Die RPCs aus Nr. 179 melden 42501 (kein
 * Recht), P0002 (nicht gefunden) und 22023 (ungültige Eingabe); bei 22023 ist
 * der Satz der Datenbank der genaueste und wird gezeigt.
 */
export const alsMaterialFehler = (e: unknown, httpStatus?: number, kontext = ERSATZ_TEXT.unbekannt): MaterialFehler => {
    if (e instanceof MaterialFehler) return e;
    const code = fehlerCode(e);
    if (code === '42501') return new MaterialFehler('kein_recht', ERSATZ_TEXT.kein_recht);
    if (code === 'P0002') return new MaterialFehler('nicht_gefunden', ERSATZ_TEXT.nicht_gefunden);
    if (code === '22023') return new MaterialFehler('ungueltig', fehlerText(e) || ERSATZ_TEXT.ungueltig);
    if (httpStatus === 0 || istNetzFehler(e)) return new MaterialFehler('netz', ERSATZ_TEXT.netz);
    if (istNichtEingerichtet(e, httpStatus)) return new MaterialFehler('nicht_eingerichtet', ERSATZ_TEXT.nicht_eingerichtet);
    return new MaterialFehler('unbekannt', `${kontext} (${describeError(e)})`);
};

/** Ruft eine RPC und gibt `data` zurück; jeder Fehler als MaterialFehler. */
const rufe = async (name: string, params: Record<string, unknown>, kontext: string): Promise<unknown> => {
    let antwort: unknown;
    try {
        antwort = await supabase.rpc(name, params);
    } catch (e) {
        throw alsMaterialFehler(e, undefined, kontext);
    }
    const error = feld(antwort, 'error');
    const status = feld(antwort, 'status');
    if (error) throw alsMaterialFehler(error, typeof status === 'number' ? status : undefined, kontext);
    return feld(antwort, 'data') ?? null;
};

// ─── Zeilen lesen (snake_case → camelCase) ──────────────────────────────────

type Zeile = Record<string, unknown>;

/** jsonb kommt als Objekt; ein JSON-Text (älterer Treiber, Proxy) wird gelesen. */
const alsObjekt = (v: unknown): Zeile | null => {
    let w = v;
    if (typeof w === 'string') {
        try {
            w = JSON.parse(w);
        } catch {
            return null;
        }
    }
    return w !== null && typeof w === 'object' && !Array.isArray(w) ? (w as Zeile) : null;
};

const textOderNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const pflicht = (v: unknown): string | null => textOderNull(v);

const STATUS = new Set<MaterialStatus>(['offen', 'bestellt', 'erledigt']);

/** Zeile `material_artikel` (Tabelle oder RPC-Antwort) → MaterialArtikel; unlesbar → null. */
export const artikelAusZeile = (roh: unknown): MaterialArtikel | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = pflicht(z.id);
    const name = pflicht(z.name);
    const qrToken = pflicht(z.qr_token);
    if (!id || !name || !qrToken) return null;
    return {
        id,
        companyId: textOderNull(z.company_id) ?? '',
        name,
        lagerort: textOderNull(z.lagerort),
        hinweis: textOderNull(z.hinweis),
        qrToken,
        // Fehlt die Spalte, gilt der Vorgabewert der Tabelle (true).
        aktiv: z.aktiv !== false,
        erstelltAm: textOderNull(z.erstellt_am) ?? '',
        geaendertAm: textOderNull(z.geaendert_am) ?? '',
    };
};

/** Zeile `material_meldungen` (Tabelle oder RPC-Antwort) → MaterialMeldung; unlesbar → null. */
export const meldungAusZeile = (roh: unknown): MaterialMeldung | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    const id = pflicht(z.id);
    const artikelId = pflicht(z.artikel_id);
    const gemeldetAm = pflicht(z.gemeldet_am);
    const status = z.status;
    if (!id || !artikelId || !gemeldetAm) return null;
    if (typeof status !== 'string' || !STATUS.has(status as MaterialStatus)) return null;
    const anzahlRoh = typeof z.anzahl === 'number' ? z.anzahl : Number(z.anzahl);
    return {
        id,
        artikelId,
        companyId: textOderNull(z.company_id) ?? '',
        gemeldetVon: textOderNull(z.gemeldet_von),
        melderName: textOderNull(z.melder_name),
        gemeldetAm,
        anzahl: Number.isFinite(anzahlRoh) && anzahlRoh >= 1 ? Math.floor(anzahlRoh) : 1,
        zuletztAusgeloestAm: textOderNull(z.zuletzt_ausgeloest_am) ?? gemeldetAm,
        status: status as MaterialStatus,
        bestelltVon: textOderNull(z.bestellt_von),
        bestelltAm: textOderNull(z.bestellt_am),
        erledigtVon: textOderNull(z.erledigt_von),
        erledigtAm: textOderNull(z.erledigt_am),
        mailGesendetAm: textOderNull(z.mail_gesendet_am),
        mailFehler: textOderNull(z.mail_fehler),
    };
};

/** Ergebnis von `material_empfaenger_anzeige` → Anzeige; unlesbar → null. */
export const empfaengerAusRpc = (roh: unknown): MaterialEmpfaengerAnzeige | null => {
    const z = alsObjekt(roh);
    if (!z) return null;
    if (z.quelle !== 'rolle' && z.quelle !== 'admins') return null;
    const namen = Array.isArray(z.namen)
        ? z.namen.filter((n): n is string => typeof n === 'string' && n.trim() !== '').map(n => n.trim())
        : [];
    return { quelle: z.quelle, namen };
};

const nurLesbare = <T>(zeilen: readonly unknown[], lesen: (z: unknown) => T | null): T[] => {
    const ergebnis: T[] = [];
    for (const z of zeilen) {
        const x = lesen(z);
        if (x) ergebnis.push(x);
    }
    return ergebnis;
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

/** Alle Artikel der Firma (RLS), seitenweise — Supabase kappt still bei 1000. */
const ladeArtikel = async (): Promise<MaterialArtikel[]> => {
    try {
        const zeilen = await ladeAlleSeiten<unknown>((von, bis) =>
            supabase.from(T_ARTIKEL).select(ARTIKEL_SPALTEN)
                .order('name', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis));
        return nurLesbare(zeilen, artikelAusZeile);
    } catch (e) {
        throw alsMaterialFehler(e, undefined, 'Artikel nicht geladen.');
    }
};

/** Alle nicht erledigten Meldungen — höchstens eine je Artikel, trotzdem seitenweise. */
const ladeOffene = async (): Promise<MaterialMeldung[]> => {
    try {
        const zeilen = await ladeAlleSeiten<unknown>((von, bis) =>
            supabase.from(T_MELDUNGEN).select(MELDUNG_SPALTEN)
                .neq('status', 'erledigt')
                .order('gemeldet_am', { ascending: false })
                .order('id', { ascending: true })
                .range(von, bis));
        return nurLesbare(zeilen, meldungAusZeile);
    } catch (e) {
        throw alsMaterialFehler(e, undefined, 'Meldungen nicht geladen.');
    }
};

/**
 * Verlauf: die letzten `VERLAUF_GRENZE` erledigten Meldungen, bewusst
 * begrenzt statt seitenweise. Eine Zeile mehr wird angefragt, um „es gibt
 * ältere“ anzeigen zu können.
 */
const ladeVerlauf = async (): Promise<{ meldungen: MaterialMeldung[]; gekappt: boolean }> => {
    let antwort: unknown;
    try {
        antwort = await supabase.from(T_MELDUNGEN).select(MELDUNG_SPALTEN)
            .eq('status', 'erledigt')
            .order('erledigt_am', { ascending: false, nullsFirst: false })
            .order('id', { ascending: true })
            .limit(VERLAUF_GRENZE + 1);
    } catch (e) {
        throw alsMaterialFehler(e, undefined, 'Verlauf nicht geladen.');
    }
    const error = feld(antwort, 'error');
    if (error) {
        const status = feld(antwort, 'status');
        throw alsMaterialFehler(error, typeof status === 'number' ? status : undefined, 'Verlauf nicht geladen.');
    }
    const data = feld(antwort, 'data');
    const zeilen = Array.isArray(data) ? data : [];
    return {
        meldungen: nurLesbare(zeilen.slice(0, VERLAUF_GRENZE), meldungAusZeile),
        gekappt: zeilen.length > VERLAUF_GRENZE,
    };
};

/**
 * Die heutigen Empfänger für die Kopfzeile. Fail-soft: jeder Fehler → null,
 * die Zeile entfällt dann — sie ist ein Hinweis, keine Voraussetzung.
 */
export const ladeMaterialEmpfaenger = async (): Promise<MaterialEmpfaengerAnzeige | null> => {
    try {
        return empfaengerAusRpc(await rufe('material_empfaenger_anzeige', {}, 'Empfänger nicht geladen.'));
    } catch (e) {
        console.warn('[material] Empfänger der Meldungen nicht geladen:', e);
        return null;
    }
};

export interface MaterialStand {
    artikel: MaterialArtikel[];
    /** Offene und bestellte Meldungen sowie der Verlauf (erledigt), unsortiert. */
    meldungen: MaterialMeldung[];
    /** Es gibt mehr erledigte Meldungen, als der Verlauf zeigt. */
    verlaufGekappt: boolean;
    empfaenger: MaterialEmpfaengerAnzeige | null;
}

export type MaterialLadeErgebnis =
    | { art: 'ok'; stand: MaterialStand }
    | { art: 'nicht_eingerichtet' };

/**
 * Alles für den Bereich, parallel geladen. Fehlt das SQL →
 * `nicht_eingerichtet`; jeder andere Fehler wird als MaterialFehler geworfen.
 */
export const ladeMaterialStand = async (): Promise<MaterialLadeErgebnis> => {
    try {
        const [artikel, offene, verlauf, empfaenger] = await Promise.all([
            ladeArtikel(),
            ladeOffene(),
            ladeVerlauf(),
            ladeMaterialEmpfaenger(),
        ]);
        return {
            art: 'ok',
            stand: { artikel, meldungen: [...offene, ...verlauf.meldungen], verlaufGekappt: verlauf.gekappt, empfaenger },
        };
    } catch (e) {
        if (e instanceof MaterialFehler && e.code === 'nicht_eingerichtet') return { art: 'nicht_eingerichtet' };
        throw e;
    }
};

/**
 * Zahl der offenen (nicht erledigten) Meldungen für das Badge im Menü —
 * head-only, es kommt keine Zeile über die Leitung (Muster
 * `zaehleOffeneFreimeldungen`). null bei JEDEM Fehler, auch bei fehlender
 * Tabelle: das Badge entfällt dann, die App läuft weiter.
 */
export async function zaehleOffeneMaterialMeldungen(): Promise<number | null> {
    try {
        const antwort: unknown = await supabase
            .from(T_MELDUNGEN)
            .select('id', { count: 'exact', head: true })
            .neq('status', 'erledigt');
        if (feld(antwort, 'error')) return null;
        const count = feld(antwort, 'count');
        return typeof count === 'number' && Number.isFinite(count) ? count : null;
    } catch {
        return null;
    }
}

// ─── Schreiben (nur RPCs, Nr. 179) ──────────────────────────────────────────

/**
 * Anlegen (`id` null) oder Ändern. Die Werte kommen bereinigt aus
 * `pruefeArtikelEingabe`; `aktiv` false macht die Karte ungültig, der
 * Verlauf bleibt. Rückgabe: die gespeicherte Zeile.
 */
export const speichereMaterialArtikel = async (id: string | null, werte: ArtikelWerte, aktiv: boolean): Promise<MaterialArtikel> => {
    const data = await rufe('material_artikel_speichern', {
        p_id: id,
        p_name: werte.name,
        p_lagerort: werte.lagerort,
        p_hinweis: werte.hinweis,
        p_aktiv: aktiv,
    }, 'Artikel nicht gespeichert.');
    const artikel = artikelAusZeile(data);
    if (!artikel) throw new MaterialFehler('unbekannt', 'Artikel gespeichert, aber die Antwort der Datenbank ist unlesbar — bitte neu laden.');
    return artikel;
};

/** Neuer QR-Token: alle gedruckten Karten dieses Artikels werden ungültig. */
export const erneuereMaterialToken = async (artikelId: string): Promise<string> => {
    const data = await rufe('material_token_neu', { p_artikel_id: artikelId }, 'Karte nicht erneuert.');
    if (!istMaterialToken(data)) {
        throw new MaterialFehler('unbekannt', 'Karte erneuert, aber die Antwort der Datenbank ist unlesbar — bitte neu laden.');
    }
    return data;
};

/** „Bestellt“ bzw. „Aufgefüllt“ (`erledigt`). Rückgabe: die geänderte Meldung. */
export const setzeMaterialStatus = async (meldungId: string, status: StatusZiel): Promise<MaterialMeldung> => {
    const data = await rufe('material_status_setzen', { p_meldung_id: meldungId, p_status: status }, 'Status nicht gesetzt.');
    const meldung = meldungAusZeile(data);
    if (!meldung) throw new MaterialFehler('unbekannt', 'Status gesetzt, aber die Antwort der Datenbank ist unlesbar — bitte neu laden.');
    return meldung;
};
